import { OrderStatus } from '@core/constants/order.constant';
import { ShipmentStatus } from '@core/constants/shipment.constant';
import { OrderForSelect } from '@core/models/order.model';

export type ShipmentPartner = {
  id: string;
  name: string;
};

export type Shipment = {
  id: string;
  shipmentReference: string;
  status: ShipmentStatus;
  sender: ShipmentPartner | null;
  receiver: ShipmentPartner | null;
  trackingNumber: string | null;
  shipFromCity: string | null;
  shipFromCountry: string | null;
  shipToCity: string | null;
  shipToCountry: string | null;
  shipDate: string | null;
  deliveryDate: string | null;
  actualDeliveryDate: string | null;
  lastStatusUpdateAt: string | null;
  shippingAccountName: string | null;
  poReferences: string[];
  linkedOrders: OrderForSelect[];
  createdAt: string;
  updatedAt: string;
};

export type ShipmentKpis = {
  onTimeDeliveryPct: number | null;
  onRouteCount: number;
  lateCount: number;
  awaitingUpdateCount: number;
};

export type ShipmentSortField = 'createdAt' | 'shipDate' | 'deliveryDate';

export type ShipmentFilters = {
  page?: number;
  limit?: number;
  status?: ShipmentStatus[];
  sender?: string;
  receiver?: string;
  term?: string;
  sort?: ShipmentSortField;
  direction?: 'ASC' | 'DESC';
};

export type CreateShipmentPayload = {
  shipmentReference: string;
  senderId?: string;
  receiverId?: string;
  trackingNumber?: string;
  trackingLink?: string;
  shipDate?: string;
  deliveryDate?: string;
  shipFromCity?: string;
  shipFromAddress?: string;
  shipToCity?: string;
  shipToAddress?: string;
};

export type UpdateShipmentPayload = {
  senderId?: string;
  receiverId?: string;
  status?: ShipmentStatus;
  trackingNumber?: string | null;
  trackingLink?: string | null;
  shipDate?: string | null;
  deliveryDate?: string | null;
  shipFromCity?: string | null;
  shipFromAddress?: string | null;
  shipToCity?: string | null;
  shipToAddress?: string | null;
  shippingAccountName?: string | null;
};

export type ShipmentOrderOption = {
  id: string;
  senderRef: string;
  receiverRef: string;
};

export type ShipmentExceptionItem = {
  id: string;
  shipmentReference: string;
  shipFromCity: string | null;
  shipFromCountry: string | null;
  shipToCity: string | null;
  shipToCountry: string | null;
  sender: ShipmentPartner | null;
  receiver: ShipmentPartner | null;
  status: ShipmentStatus;
  daysSinceLastUpdate: number | null;
  deliveryDate: string | null;
  actualDeliveryDate: string | null;
};

export type ShipmentExceptions = {
  late: ShipmentExceptionItem[];
  awaitingUpdate: ShipmentExceptionItem[];
  total: number;
};

export type ShipmentLine = {
  id: string;
  senderSku: string | null;
  receiverSku: string | null;
  productDescription: string | null;
  quantity: number | null;
};

export type TrackingStatus = string;

export type ShipmentTracking = {
  id: string;
  status: TrackingStatus;
  createdAt: string;
};

export type ShipmentDetail = Omit<Shipment, 'linkedOrders'> & {
  trackingLink: string | null;
  shipFromAddress: string | null;
  shipFromLat: number | null;
  shipFromLng: number | null;
  shipToAddress: string | null;
  shipToLat: number | null;
  shipToLng: number | null;
  packagingCount: number | null;
  packingType: string | null;
  linkedOrders: ShipmentLinkedOrder[];
  lines: ShipmentLine[];
  trackingHistory: ShipmentTracking[];
};

export type ShipmentEditableField = 'shipFromAddress' | 'shipFromCity' | 'shipToAddress' | 'shipToCity'
  | 'shipDate' | 'deliveryDate' | 'trackingNumber' | 'trackingLink' | 'shippingAccountName';

export type ShipmentLinkedOrderLine = {
  senderSku: string | null;
  receiverSku: string | null;
  productName: string | null;
  quantity: number | null;
};

export type ShipmentLinkedOrder = {
  id: string;
  senderRef: string;
  receiverRef: string | null;
  senderName: string;
  receiverName: string;
  status: OrderStatus;
  receiverStatus: string | null;
  orderValue: number | null;
  createdAt: string;
  confirmedAt: string | null;
  completedAt: string | null;
  lines: ShipmentLinkedOrderLine[];
};
